/**
 * @file index.ts
 * @description 信息面板
 * @author xingwei
 * @createTime 2025-08-22
 * @lastEditTime 2025-08-22
 */

import _ from 'lodash'
import { Group, Vector3, createLabel } from '@anov/3d-core'
import ObjectLayer from '../Base/ObjectLayer'
import type { ContentOption, IPanel, Option, PanelType, TitleOption } from './type'

const DEFAULT_OPTION = {
  position: [0, 0, 0],
  type: 'info',
  title: {
    value: '面板标题',
    fontSize: '20px',
  },
  content: {
    value: '面板内容',
    fontSize: '16px',
  },
  width: 300,
  height: 200,
  bgColor: '#fff',
  borderRadius: 8,
  closable: true,
  padding: '10px',
}

class InfoPanel extends ObjectLayer implements IPanel {
  private _title: TitleOption
  private _content: ContentOption
  private _width: number
  private _height: number
  private _bgColor: string
  private _position: Array<number>
  private _type: PanelType
  private _borderRadius = 10
  private _bgUrl: string
  private _className: string
  private _customStyle: any
  private _closeable: boolean
  private _padding: string
  private _group: Group
  private _onClose: Function
  visible = false

  constructor(option: Option, sceneControl?: any) {
    super(sceneControl)
    const params = _.mergeWith(_.cloneDeep(DEFAULT_OPTION), option)
    const {
      title: _title,
      content: _content,
      width: _width,
      height: _height,
      bgColor: _bgColor,
      position: _position,
      type: _type,
      bgUrl: _bgUrl,
      className: _className,
      customStyle: _customStyle,
      borderRadius: _borderRadius,
      padding: _padding,
      closable: _closeable,
      onClose: _onClose,
    } = params

    Object.assign(this, {
      _title,
      _content,
      _width,
      _height,
      _bgColor,
      _position,
      _type,
      _bgUrl,
      _className,
      _customStyle,
      _borderRadius,
      _padding,
      _closeable,
      _onClose,
    })

    this._group = new Group()
    this._group.visible = this.visible
    this.init()
  }

  private init() {
    this.createBox()
  }

  private createBox() {
    // 使用 DocumentFragment 减少重排
    // const fragment = document.createDocumentFragment();
    const bgDom = this.createBgDom() // 创建容器盒子
    const headerDom = this.createHeaderDom() // 创建头部盒子

    // 创建内容
    const contentDom = document.createElement('div')
    contentDom.className = 'panel-content'
    if (typeof this._content.value === 'string')
      contentDom.textContent = this._content.value

    else if (this._content.value instanceof HTMLElement)
      contentDom.appendChild(this._content.value)

    const { value, ...contentStyle } = this._content
    // 正确设置样式的方式
    Object.assign(contentDom.style, contentStyle) // 待测试

    bgDom.appendChild(headerDom)
    bgDom.appendChild(contentDom)

    const bgObject = createLabel(bgDom)
    bgObject.position.set(0, 0, 0)
    bgObject.center.set(0, 0)

    this._group.add(bgObject)

    this.add(this._group)
    this.applyPosition() // 初始化自动定位
  }

  /** 创建背景 */
  private createBgDom() {
    // 创建容器盒子
    const bgDom = document.createElement('div')
    Object.assign(bgDom.style, {
      width: `${this._width}px`,
      height: `${this._height}px`,
      // left: `${this._position[0]}px`,
      // top: `${this._position[1]}px`,
      overflow: 'hidden',
      padding: this._padding,
      borderRadius: this._bgUrl ? '' : `${this._borderRadius}px`,
      backgroundColor: this._bgUrl ? '' : this._bgColor,
      backgroundImage: this._bgUrl ? `url(${this._bgUrl})` : '',
      backgroundSize: this._bgUrl ? 'contain' : '',
      backgroundRepeat: this._bgUrl ? 'no-repeat' : '',
      boxSizing: 'border-box',

    })
    if (this._className)
      bgDom.className = this._className
    if (this._customStyle)
      Object.assign(bgDom.style, this._customStyle)
    return bgDom
  }

  /** 创建头部 */
  private createHeaderDom() {
    const headerDom = document.createElement('div')
    headerDom.className = 'panel-header'
    headerDom.style.display = 'flex'
    headerDom.style.alignItems = 'center'
    // 创建标题
    const titleDom = document.createElement('p')
    titleDom.className = 'panel-title'
    titleDom.style.display = 'flex'
    titleDom.style.flex = '1'
    titleDom.textContent = this._title.value
    const { value: titleValue, ...titleStyle } = this._title
    // 正确设置样式的方式
    Object.assign(titleDom.style, titleStyle) // 待测试

    headerDom.appendChild(titleDom)

    // 创建关闭按钮
    if (this._closeable) {
      const closeDom = document.createElement('div')
      closeDom.className = 'panel-close'
      closeDom.style.cursor = 'pointer'
      closeDom.style.fontSize = this._title.fontSize
      closeDom.style.color = this._title.color
      closeDom.textContent = 'x'
      closeDom.style.pointerEvents = 'auto'
      closeDom.onclick = () => {
        this.close()
      }
      headerDom.appendChild(closeDom)
    }
    return headerDom
  }

  /** 自动定位面板 */
  private applyPosition(offset: Vector3 = new Vector3(0, 0, 0)) {
    if (Array.isArray(this._position)) {
      // 如果是数组 [x, y]
      const [x, y, z] = this._position
      this._group.position.set(x, y, z) // 注意y轴取反
    }
  }

  public open() {
    this.visible = true
    this._group.visible = true

    // 同步 DOM 节点显示
    // this._group.children.forEach((child: any) => {
    //     if (child.element) {
    //         child.element.style.display = 'block';
    //     }
    // });
  }

  public close() {
    this.visible = false
    this._group.visible = false

    // 同步 DOM 节点隐藏
    // this._group.children.forEach((child: any) => {
    //     if (child.element) {
    //         child.element.style.display = 'none';
    //     }
    // });
    if (this._onClose)
      this._onClose()
  }

  public update(option: Option) {
    const merged = _.mergeWith(_.cloneDeep(DEFAULT_OPTION), option)
    this._title = merged.title
    this._content = merged.content
    this._width = merged.width
    this._height = merged.height
    this._bgColor = merged.bgColor
    this._borderRadius = merged.borderRadius || 0
    this._bgUrl = merged.bgUrl
    this._className = merged.className
    this._customStyle = merged.style

    this._group.clear()
    this.init()
  }

  destroy(): void {
    this._group.clear()
    this.remove(this._group)
    // 清理DOM引用
    this._group.children.forEach((child: any) => {
      if (child.element && child.element.parentNode)
        child.element.parentNode.removeChild(child.element)
    })
  }
}

export { InfoPanel }